import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  FileText,
  Pin,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Plus,
  Sparkles,
  StickyNote,
  Trash2,
  Voicemail,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { callBackend, invalidateQueries, useMutation, useQueryState } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { useCrm } from './CrmContext';
import { buttonClass, errorMessage, formatTalk, formatWhen, inputClass } from './format';
import type { CallSummary, CrmCall, CrmNote, CrmTask, TimelineItem } from './types';

type TimelineResult = { items: TimelineItem[]; contactNames: Record<string, string>; nextBeforeMs?: number };

function CallRow({ call, summary, contactName }: { call: CrmCall; summary?: CallSummary; contactName?: string }) {
  const { data } = useCrm();
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [taskText, setTaskText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const retrySummary = useMutation(backendApi.crmAi.retrySummary);
  const missed = call.direction === 'inbound' && !call.answered;
  const Icon = call.voicemail ? Voicemail : missed ? PhoneMissed : call.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const color = missed ? 'text-red-600 bg-red-50 dark:bg-red-900/30' : call.direction === 'inbound' ? 'text-green-600 bg-green-50 dark:bg-green-900/30' : 'text-primary-600 bg-primary-50 dark:bg-primary-900/30';
  const who = call.answeredByName ? `${call.answeredByName} (${call.answeredBy})` : call.answeredBy;
  const text = call.voicemail
    ? 'Left a voicemail'
    : missed
      ? call.extensions.length
        ? `Missed call · rang ${call.extensions.slice(0, 5).join(', ')}`
        : 'Missed call · hung up before reaching anyone'
      : call.direction === 'inbound'
        ? `Called in · answered by ${who ?? 'staff'} · ${formatTalk(call.talkSeconds)}`
        : call.answered
          ? `Called by ${who ?? 'staff'} · ${formatTalk(call.talkSeconds)}`
          : `Call attempt by ${call.extensions[0] ?? 'staff'} · not answered`;

  const loadTranscript = async () => {
    if (!summary) return;
    if (transcript === null) {
      try {
        const result = await callBackend<{ transcript: string | null }>(backendApi.crmAi.getTranscript, { summaryId: summary.id });
        setTranscript(result.transcript ?? 'The transcript has been removed (retention period).');
      } catch (error) {
        toast.error(errorMessage(error));
        return;
      }
    }
    setShowTranscript((open) => !open);
  };

  const makeTask = async (item: string) => {
    if (!summary) return;
    setBusy(true);
    try {
      await callBackend(backendApi.crmAi.makeTaskFromActionItem, { summaryId: summary.id, item });
      invalidateQueries();
      toast.success('Follow-up added');
      setTaskText(null);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 dark:text-white">
          {contactName && <span className="font-medium">{contactName}: </span>}
          {text}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatWhen(call.startedAtMs)} · {call.number}
        </p>
        {summary?.status === 'done' && (
          <div className="mt-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 p-3 text-sm text-purple-950 dark:text-purple-100">
            <p className="flex items-center gap-1 text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
              <Sparkles className="w-3 h-3" /> AI summary{summary.sentiment && summary.sentiment !== 'neutral' ? ` · ${summary.sentiment}` : ''}
            </p>
            <p>{summary.summary}</p>
            {summary.actionItems.length > 0 && (
              <ul className="mt-2 space-y-1">
                {summary.actionItems.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs">
                    <span className="flex-1">• {item}</span>
                    {data?.canManage && (
                      <button type="button" onClick={() => setTaskText(item)} className="shrink-0 text-purple-700 dark:text-purple-300 hover:underline">
                        Review task
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {taskText !== null && <div className="mt-3 space-y-2">
              <label className="block text-xs">Review task title from this call
                <textarea value={taskText} onChange={(event) => setTaskText(event.target.value)} maxLength={200} rows={2} className={`${inputClass} mt-1`} />
              </label>
              <div className="flex gap-2"><button type="button" disabled={busy || !taskText.trim()} onClick={() => void makeTask(taskText)} className={buttonClass.small}>Create follow-up</button><button type="button" onClick={() => setTaskText(null)} className={buttonClass.small}>Cancel</button></div>
            </div>}
            {summary.hasTranscript && (
              <button type="button" onClick={() => void loadTranscript()} className="mt-2 inline-flex items-center gap-1 text-xs text-purple-700 dark:text-purple-300 hover:underline">
                <FileText className="w-3 h-3" /> {showTranscript ? 'Hide transcript' : 'Transcript'}
              </button>
            )}
            {showTranscript && transcript && <p className="mt-2 text-xs whitespace-pre-line max-h-64 overflow-y-auto">{transcript}</p>}
          </div>
        )}
        {summary?.status === 'pending' && <p className="mt-1 text-xs text-purple-600 dark:text-purple-300">AI summary on its way…</p>}
        {(summary?.status === 'error' || summary?.status === 'skipped') && <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">Summary {summary.status === 'error' ? 'failed' : 'unavailable'}{summary.error ? `: ${summary.error}` : ''}. {data?.canManage && <button type="button" className="underline" onClick={() => void retrySummary({ summaryId: summary.id }).then(() => toast.success('Summary queued again')).catch((error) => toast.error(errorMessage(error)))}>Retry</button>}</p>}
      </div>
    </>
  );
}

function NoteRow({ note }: { note: CrmNote }) {
  const { data } = useCrm();
  const updateNote = useMutation(backendApi.crm.updateNote);
  const deleteNote = useMutation(backendApi.crm.deleteNote);
  return (
    <>
      <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-amber-600 bg-amber-50 dark:bg-amber-900/30">
        <StickyNote className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line break-words">{note.body}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          {note.pinned && <Pin className="w-3 h-3 text-amber-600" />}
          {note.authorName} · {formatWhen(note.createdAtMs)}
          {data?.canManage && (
            <>
              <button type="button" className="hover:underline" onClick={() => void updateNote({ noteId: note.id, pinned: !note.pinned }).catch((error) => toast.error(errorMessage(error)))}>
                {note.pinned ? 'Unpin' : 'Pin'}
              </button>
              <button
                type="button"
                className="hover:text-red-600"
                aria-label="Delete note"
                onClick={() => {
                  if (confirm('Delete this note?')) void deleteNote({ noteId: note.id }).catch((error) => toast.error(errorMessage(error)));
                }}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          )}
        </p>
      </div>
    </>
  );
}

function TaskRow({ task }: { task: CrmTask }) {
  const done = task.status !== 'open';
  const text =
    task.kind === 'callback'
      ? `${task.missedCount && task.missedCount > 1 ? `${task.missedCount} missed calls` : 'Missed call'} → callback ${
          task.status === 'open'
            ? 'open'
            : task.resolution === 'called_back'
              ? `done: called back by ${task.resolvedBy ?? 'staff'}`
              : task.resolution === 'caller_reached'
                ? `done: they called again and got through (${task.resolvedBy ?? 'staff'})`
                : task.status === 'dismissed'
                  ? `dismissed by ${task.resolvedBy ?? 'staff'}`
                  : `done by ${task.resolvedBy ?? 'staff'}`
        }`
      : `${task.title}${done ? ` · ${task.status === 'done' ? 'done' : 'dismissed'}${task.resolvedBy ? ` by ${task.resolvedBy}` : ''}` : ''}`;
  return (
    <>
      <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${done ? 'text-gray-500 bg-gray-100 dark:bg-gray-700' : 'text-orange-600 bg-orange-50 dark:bg-orange-900/30'}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 dark:text-white">{text}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatWhen(task.kind === 'callback' ? task.lastMissedAtMs ?? task.createdAtMs : task.createdAtMs)}
          {task.createdBy ? ` · added by ${task.createdBy}` : ''}
        </p>
      </div>
    </>
  );
}

/** Calls, notes, tasks and AI summaries of a contact or company, with a note box on top. */
export function Timeline({ contactId, companyId }: { contactId?: string; companyId?: string }) {
  const { data } = useCrm();
  const addNote = useMutation(backendApi.crm.addNote);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [beforeMs, setBeforeMs] = useState<number | undefined>(undefined);
  const [older, setOlder] = useState<TimelineItem[]>([]);
  const first = useQueryState<TimelineResult>(backendApi.crm.getTimeline, { contactId, companyId });
  const more = useQueryState<TimelineResult>(backendApi.crm.getTimeline, beforeMs ? { contactId, companyId, beforeMs } : 'skip');

  useEffect(() => {
    setOlder([]);
    setBeforeMs(undefined);
  }, [contactId, companyId]);
  useEffect(() => {
    if (more.data) setOlder((current) => [...current, ...more.data!.items.filter((item) => !current.some((existing) => existing.atMs === item.atMs && existing.type === item.type))]);
  }, [more.data]);

  const items = [...(first.data?.items ?? []), ...older];
  const nextBeforeMs = (more.data ?? first.data)?.nextBeforeMs;
  const names = { ...(first.data?.contactNames ?? {}), ...(more.data?.contactNames ?? {}) };

  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await addNote({ contactId, companyId, body: note });
      setNote('');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {data?.canManage && (
        <div className="space-y-2">
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void save();
            }}
            rows={2}
            placeholder="Add a note (Ctrl+Enter to save)"
            className="w-full text-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
          />
          {note.trim() && (
            <button type="button" onClick={() => void save()} disabled={saving} className={buttonClass.primary}>
              <Plus className="w-4 h-4" /> Save note
            </button>
          )}
        </div>
      )}
      {first.error && <p className="text-sm text-red-600">{first.error}</p>}
      {first.isLoading && <p className="text-sm text-gray-500">Loading history…</p>}
      {!first.isLoading && items.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No calls or notes yet.</p>}
      <ol className="space-y-4">
        {items.map((item) => (
          <li key={`${item.type}-${item.type === 'call' ? item.call.id : item.type === 'note' ? item.note.id : item.task.id}`} className="flex gap-3">
            {item.type === 'call' && <CallRow call={item.call} summary={item.summary} contactName={item.call.contactId ? names[item.call.contactId] : undefined} />}
            {item.type === 'note' && <NoteRow note={item.note} />}
            {item.type === 'task' && <TaskRow task={item.task} />}
          </li>
        ))}
      </ol>
      {nextBeforeMs && (
        <button type="button" onClick={() => setBeforeMs(nextBeforeMs)} disabled={more.isLoading} className={buttonClass.secondary}>
          <ChevronDown className="w-4 h-4" /> {more.isLoading ? 'Loading…' : 'Older'}
        </button>
      )}
    </div>
  );
}
